import React from "react";
import { t } from "ttag";

import { MasonryGrid } from "../../../common/MasonryGrid";
import { IProduct } from "../../../models/catalogue.interfaces";
import { useAppSelector } from "../../reducers";
import { defaults } from "../defaults";
import { GridFilterConfig } from "../filters";
import { SortDirection } from "../models";
import { IProductCardType } from "../models.interfaces";
import { GridSorterConfig, PriceGridSorter } from "../sorters";
import { ProductCard } from "./ProductCard";

import styles from "./GridContent.module.scss";

interface IProps {
    gridVariant: string;
    cardType: IProductCardType;
    filters: GridFilterConfig[];
    sorters?: GridSorterConfig[];
    disableHoverOverlay?: boolean;
    buildExternalCallout?: (rootProduct: IProduct) => React.ReactNode;
}

const getSorter = (
    sorters: GridSorterConfig[] | undefined,
    sortMethod: string | null,
): GridSorterConfig => {
    let sorter: GridSorterConfig | undefined;
    if (sorters) {
        sorter = sorters.find((s) => s.id === sortMethod);
    }
    if (!sorter && sorters) {
        sorter = sorters[0];
    }
    if (!sorter) {
        sorter = new PriceGridSorter({
            id: "price-low-to-high",
            // TODO(i18n): Wrap in t tagged template: t`Price Low to High`
            label: "Price Low to High",
            direction: SortDirection.ASC,
        });
    }
    return sorter;
};

export const GridContent = (props: IProps): React.ReactNode => {
    const state = useAppSelector(
        (rootState) => rootState.productgrid2 || defaults,
    );
    const products = state.products;
    const filterState = state.filters;
    const sortMethod = state.sortMethod;

    const filteredProducts = Array.from(
        props.filters.reduce<Iterable<IProduct>>((products, nextFilter) => {
            const selectedValues = new Set(
                filterState[nextFilter.filterID]?.selectedValues,
            );
            return nextFilter.applyFilter(selectedValues, products);
        }, products),
    );
    const sorter = getSorter(props.sorters, sortMethod);
    const sortedProducts = sorter.sort(filteredProducts);

    return (
        <>
            {props.gridVariant === "sleep-outfitters" && (
                <header>
                    <div className={styles.resultsLabel}>
                        {/* TODO(i18n): Use ngettext for plurals: ngettext(msgid`${sortedProducts.length} result`, `${sortedProducts.length} results`, sortedProducts.length) */}
                        {sortedProducts.length}{" "}
                        {sortedProducts.length === 1 ? "result" : "results"}
                    </div>
                    {filterState.brand &&
                        filterState.brand.selectedValues.length > 0 && (
                            <h2 className={styles.brandTitle}>
                                {filterState.brand.selectedValues.join(", ")}
                            </h2>
                        )}
                </header>
            )}
            <MasonryGrid
                className={styles.container}
                itemSelector={".product-grid-card"}
                entities={sortedProducts}
                getTileID={(product) => `${product.id}`}
                buildTile={(product, ref) => (
                    <ProductCard
                        forwardedRef={ref}
                        key={`${product.id}`}
                        gridVariant={props.gridVariant}
                        cardType={props.cardType}
                        rootProduct={product}
                        disableHoverOverlay={props.disableHoverOverlay}
                        buildExternalCallout={props.buildExternalCallout}
                    />
                )}
                emptyState={
                    <div>
                        <p className={styles.emptyMessageHeading}>
                            {t`WE CAN'T FIND A PRODUCT THAT MATCHES YOUR SEARCH.`}
                        </p>
                        <p className={styles.emptyMessageBody}>
                            {t`Sorry about that! Please try your search again with another set of filters.`}
                        </p>
                    </div>
                }
                options={{
                    horizontalOrder: true,
                    gutter: props.gridVariant === "sleep-outfitters" ? 22 : 0,
                }}
                forceEqualHeight={true}
            />
        </>
    );
};
